import { Box, Typography } from '@mui/material'

/**
 * La marque IOBIZ, et son éditeur.
 *
 * ⭐ Un seul composant pour l'entête et le pied de page : la mention « eduqor »
 * doit rester identique partout, et deux copies divergent toujours — l'une est
 * corrigée, l'autre oubliée.
 *
 * La signature diffère par la place : « par eduqor » sous le logo de l'entête,
 * où l'espace est compté ; « Propulsé par eduqor » en pied, où la formule peut
 * respirer.
 */
export function Marque({
  mention,
  taille = 'normale',
  centre = false,
}: {
  /** Ce qui s'inscrit sous le nom. */
  mention: string
  taille?: 'normale' | 'grande'
  centre?: boolean
}): JSX.Element {
  const pastille = taille === 'grande' ? 40 : 30
  const nom = taille === 'grande' ? 22 : 17

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        ...(centre ? { justifyContent: 'center' } : {}),
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: pastille,
          height: pastille,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: 'primary.main',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 800,
          fontSize: pastille * 0.4,
          letterSpacing: '-.02em',
        }}
      >
        IO
      </Box>
      <Box sx={{ lineHeight: 1.1 }}>
        <Typography
          component="div"
          sx={{ fontWeight: 700, fontSize: nom, letterSpacing: '-.02em', lineHeight: 1.1 }}
        >
          IOBIZ
        </Typography>
        <Typography
          component="div"
          variant="caption"
          sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: 11 }}
        >
          {mention}
        </Typography>
      </Box>
    </Box>
  )
}

/**
 * Pied de page, présent sur TOUTES les pages — y compris celles où l'on n'est
 * pas connecté. C'est la seule signature de l'éditeur que verra quelqu'un qui
 * s'arrête à l'écran de connexion.
 */
export function PiedDePage(): JSX.Element {
  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        py: 5,
        px: 4,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Marque mention="Propulsé par eduqor" centre />
      <Typography
        variant="caption"
        sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 2 }}
      >
        © {new Date().getFullYear()} — Gestion commerciale multi-boutiques
      </Typography>
    </Box>
  )
}

/**
 * Coquille des pages SANS menu latéral : connexion, choix de boutique, console
 * plateforme, définition du mot de passe.
 *
 * ⚠️ Elle porte l'entête et le pied ; les pages sous `AppLayout` ont déjà leur
 * barre et ne doivent surtout pas passer par ici — deux entêtes empilés.
 */
export function CoquilleSimple({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box
        component="header"
        sx={{
          px: 4,
          py: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Marque mention="par eduqor" />
      </Box>

      <Box sx={{ flex: 1 }}>{children}</Box>

      <PiedDePage />
    </Box>
  )
}
